/**
 * Prices as the BUYER reads them: every row incl. VAT, and one "varav moms"
 * line for the whole order (P25009-475).
 *
 * Buyer prices are always VAT-inclusive, but the line items are not built that
 * way. getVATLineItem (server/api-util/lineItems.js) splits each item's listed
 * price into a NET item line and one merged `line-item/vat` line, so a 1 000 kr
 * tyre arrives as 800 + 200. Freight and add-ons (montering) are stored at
 * their full price and never split. Printing the line items as they are is how
 * checkout came to show "Enhetspris 800 kr … VAT (Moms) 200 kr".
 *
 * This module puts each item's VAT back into its own row and counts the VAT of
 * the whole order — for display only. It changes no line item, so the money
 * (payin, payout, commission, the Stripe charge) is exactly what it was.
 *
 * The VAT rules (P25009-468, Christofer 2026-09-24):
 *   - A company seller charges 25 % on everything it sells: the goods, and the
 *     freight and montering billed with them.
 *   - A private seller is not registered for VAT, so nothing they sell carries
 *     any: not the goods, new or used, and not the freight. The ITEM rule is
 *     the server's (isVatApplicable) and arrives here as the VAT line, whatever
 *     it says — an order placed before P25009-468 keeps the VAT line it has.
 *   - Wheelplace's own commission always carries 25 %. The buyer only ever
 *     sees a customer commission, which is not turned on in Console; whether
 *     its price would include the VAT is undecided, so it is not counted here.
 *
 * BR-3's example: a company's 1 000 kr tyre + 399 kr montering + 200 kr
 * freight = 1 599 kr, varav moms 200 + 79,80 + 40 = 319,80 kr.
 *
 * Each share uses the server's own rounding (net = round(gross / 1.25)), so the
 * rows add up to what the buyer pays, to the öre.
 *
 * CommonJS with no imports, like freightCore.js: the API server requires it
 * for the order email, and the browser bundles it for checkout and the listing
 * page. One rule, or the three disagree about the VAT.
 *
 * MONEY UNIT CONTRACT: integer öre (subunits) in and out.
 */

const DEFAULT_VAT_RATE = 25;

const LINE_ITEM_VAT = 'line-item/vat';

/* Lines whose listed price the server splits into a net line + the VAT line.
 * The primary listing's code follows its unit type. */
const VAT_SPLIT_CODES = [
  'line-item/item',
  'line-item/cart-item',
  'line-item/day',
  'line-item/night',
  'line-item/hour',
];

/* Lines stored at their full price. They carry VAT when the seller does. The
 * miljöavgift (P25009-484) is 25 kr per tyre with the VAT inside it. */
const SELLER_PRICED_CODES = [
  'line-item/shipping-fee',
  'line-item/pickup-fee',
  'line-item/service',
  'line-item/miljoavgift',
];

/* Beyond this many split rows, the search for which of them carry VAT is
 * skipped (2^12 combinations). A cart that large does not exist today. */
const MAX_SEARCHED_ROWS = 12;

/** A company seller is VAT-registered — the same test as the server's isVatApplicable. */
const isVatRegisteredSeller = user => user?.attributes?.profile?.publicData?.userType === 'business';

const amountOf = money => (money && typeof money.amount === 'number' ? money.amount : 0);

/* The SDK's quantity is a decimal, in one of two shapes. The browser's SDK
 * hands over a Decimal.js (the typeHandlers in src/util/api.js), which
 * Number() reads. The server's Integration SDK has no type handlers and hands
 * over its own BigDecimal, whose digits sit in `.value` — Number() makes NaN of
 * that, so every server-side order (the order mail, the receipt, the finance
 * ledger) read as quantity 1, and a four-tyre order's VAT no longer matched
 * its rows. */
const quantityOf = lineItem => {
  const raw = lineItem?.quantity;
  const q = Number(raw && typeof raw === 'object' && 'value' in raw ? raw.value : raw);
  return Number.isFinite(q) && q > 0 ? q : 1;
};

/* Sharetribe's default for a line with no includeFor is both sides. */
const isBuyerLine = lineItem =>
  !!lineItem && (!lineItem.includeFor || lineItem.includeFor.includes('customer'));

const factorOf = vatRate => 1 + vatRate / 100;

/** The VAT inside a VAT-inclusive amount, rounded as the server rounds. Signed. */
const vatInside = (grossSubunits, vatRate = DEFAULT_VAT_RATE) => {
  const gross = Math.abs(grossSubunits || 0);
  const vat = gross - Math.round(gross / factorOf(vatRate));
  return grossSubunits < 0 ? -vat : vat;
};

/**
 * The listed price a net unit price came from: the gross g for which the
 * server's round(g / 1.25) gives this net. Two prices one öre apart can share a
 * net, so a whole-krona price wins, as listed prices are.
 */
const grossFromNet = (netSubunits, vatRate = DEFAULT_VAT_RATE) => {
  const factor = factorOf(vatRate);
  const exact = netSubunits * factor;
  const candidates = [];
  for (let g = Math.floor(exact) - 1; g <= Math.ceil(exact) + 1; g += 1) {
    if (Math.round(g / factor) === netSubunits) candidates.push(g);
  }
  if (candidates.length === 0) return Math.round(exact);
  const wholeKrona = candidates.find(g => g % 100 === 0);
  if (wholeKrona !== undefined) return wholeKrona;
  return candidates.reduce((best, g) => (Math.abs(g - exact) < Math.abs(best - exact) ? g : best));
};

const bitCount = mask => {
  let count = 0;
  for (let m = mask; m; m >>= 1) count += m & 1;
  return count;
};

/**
 * Hands the merged VAT line back to the split rows it came from.
 *
 * Usually every row carries VAT (a company seller) or none (a private seller).
 * A private seller's order from before P25009-468 can mix a new item (VAT, as
 * new goods were always taxed then) with a used one (none), and the line
 * items alone do not say which is which — so the rows whose VAT adds up to the
 * VAT line exactly are the ones that carry it, preferring the most rows.
 * If no combination matches to the öre, the VAT line is spread by net price,
 * so the rows still add up to what the buyer pays. That is what an order from
 * before the VAT-inclusive prices looks like: its VAT was 25 % added on top of
 * the item AND the freight (a 1 500 kr item + 250 kr freight carries 437,50 kr),
 * so that VAT line already covers the whole order.
 *
 * @returns {{ perRow: Array<number>, exact: boolean }} the VAT of each row, in
 *   öre, and whether the VAT line is exactly the items' own VAT
 */
const allocateItemVat = (rows, vatLineSubunits, vatRate) => {
  if (vatLineSubunits === 0) return { perRow: rows.map(() => 0), exact: true };
  if (rows.length === 0) return { perRow: [], exact: false };

  const ifCarrying = rows.map(row => {
    const unitNet = amountOf(row.unitPrice);
    return (grossFromNet(unitNet, vatRate) - unitNet) * quantityOf(row);
  });

  const full = (1 << rows.length) - 1;
  const masks =
    rows.length <= MAX_SEARCHED_ROWS
      ? Array.from({ length: full }, (_, i) => full - i).sort((a, b) => bitCount(b) - bitCount(a))
      : [full];
  const match = masks.find(
    mask => ifCarrying.reduce((sum, vat, i) => (mask & (1 << i) ? sum + vat : sum), 0) === vatLineSubunits
  );
  if (match !== undefined) {
    return { perRow: ifCarrying.map((vat, i) => (match & (1 << i) ? vat : 0)), exact: true };
  }

  const nets = rows.map(row => amountOf(row.lineTotal));
  const netSum = nets.reduce((sum, net) => sum + net, 0);
  let left = vatLineSubunits;
  const perRow = nets.map((net, i) => {
    if (i === nets.length - 1) return left;
    const share = netSum ? Math.round((vatLineSubunits * net) / netSum) : 0;
    left -= share;
    return share;
  });
  return { perRow, exact: false };
};

/**
 * @param {Array} lineItems the order's line items; only the buyer's count
 * @param {Object} [options]
 * @param {boolean} [options.sellerChargesVat] the seller is a company (isVatRegisteredSeller)
 * @param {number} [options.vatRate]
 * @returns {{ rows: Array, vatSubunits: number, totalSubunits: number }}
 *   rows: the buyer's lines in their own order, minus refunds and the VAT
 *     line, each { lineItem, code, quantity, unitSubunits, totalSubunits,
 *     vatSubunits } with the amounts incl. VAT;
 *   vatSubunits: "varav moms" for the whole order, refunds taken off;
 *   totalSubunits: what the buyer pays, which is the payin total.
 */
const buyerPrices = (lineItems, { sellerChargesVat = false, vatRate = DEFAULT_VAT_RATE } = {}) => {
  const buyerLines = (Array.isArray(lineItems) ? lineItems : []).filter(isBuyerLine);
  const current = buyerLines.filter(li => !li.reversal);
  const refunds = buyerLines.filter(li => li.reversal);

  const vatLineSubunits = current
    .filter(li => li.code === LINE_ITEM_VAT)
    .reduce((sum, li) => sum + amountOf(li.lineTotal), 0);
  const splitRows = current.filter(li => VAT_SPLIT_CODES.includes(li.code));
  const { perRow: itemVat, exact } = allocateItemVat(splitRows, vatLineSubunits, vatRate);

  /* The VAT in a line's own amount. A split line's VAT lives in the VAT line,
   * so here it is 0; allocateItemVat hands it back for the rows. When the VAT
   * line is more than the items' own VAT (an older order), it already covers
   * the freight and add-ons, and counting them again would charge them twice.
   * An order with no VAT line at all was booked without VAT — a company's used
   * goods before P25009-430 BD-1 — so none is found in its freight either:
   * old orders stay as they were booked (P25009-468). */
  const countsSellerPricedVat = sellerChargesVat && exact && vatLineSubunits > 0;
  const vatOfSellerPriced = li =>
    countsSellerPricedVat && SELLER_PRICED_CODES.includes(li.code)
      ? vatInside(amountOf(li.lineTotal), vatRate)
      : 0;

  const rows = current
    .filter(li => li.code !== LINE_ITEM_VAT)
    .map(li => {
      const quantity = quantityOf(li);
      const splitIndex = splitRows.indexOf(li);
      if (splitIndex === -1) {
        return {
          lineItem: li,
          code: li.code,
          quantity,
          unitSubunits: li.unitPrice ? amountOf(li.unitPrice) : Math.round(amountOf(li.lineTotal) / quantity),
          totalSubunits: amountOf(li.lineTotal),
          vatSubunits: vatOfSellerPriced(li),
        };
      }
      const vat = itemVat[splitIndex];
      const total = amountOf(li.lineTotal) + vat;
      return {
        lineItem: li,
        code: li.code,
        quantity,
        unitSubunits: Math.round(total / quantity),
        totalSubunits: total,
        vatSubunits: vat,
      };
    });

  /* A refund takes its VAT back with it: the VAT line's own reversal, plus the
   * VAT inside a refunded freight or add-on. */
  const refundedVat = refunds.reduce(
    (sum, li) =>
      sum + (li.code === LINE_ITEM_VAT ? amountOf(li.lineTotal) : vatOfSellerPriced(li)),
    0
  );

  /* The VAT line itself, not the rows' shares of it: the shares add up to it
   * anyway, and the VAT still counts on the odd order with no item row. */
  const sellerPricedVat = current.reduce((sum, li) => sum + vatOfSellerPriced(li), 0);

  return {
    rows,
    vatSubunits: vatLineSubunits + sellerPricedVat + refundedVat,
    totalSubunits: buyerLines.reduce((sum, li) => sum + amountOf(li.lineTotal), 0),
  };
};

/**
 * The VAT rate an order's VAT works out to, read back from the rows that carry
 * it rather than asserted as 25, so a document's label stays true if the rate
 * ever changes. Null when the order has no VAT.
 *
 * @param {{ rows: Array, vatSubunits: number }} prices from buyerPrices
 * @returns {number|null} the rate in percent, e.g. 25
 */
const vatRateOf = ({ rows = [], vatSubunits = 0 } = {}) => {
  if (!(vatSubunits > 0)) return null;
  const base =
    rows.filter(row => row.vatSubunits > 0).reduce((sum, row) => sum + row.totalSubunits, 0) -
    vatSubunits;
  return base > 0 ? Math.round((vatSubunits / base) * 100) : null;
};

module.exports = {
  DEFAULT_VAT_RATE,
  VAT_SPLIT_CODES,
  SELLER_PRICED_CODES,
  isVatRegisteredSeller,
  quantityOf,
  vatInside,
  grossFromNet,
  buyerPrices,
  vatRateOf,
};
